import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  api,
  type DiagnosticAssessment as DiagnosticAssessmentType,
  type OnboardingAssessmentQuestion,
} from "@/lib/api";

type AnswerState = "pending" | "correct" | "incorrect" | "timeout";

export default function DiagnosticAssessmentPage() {
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<DiagnosticAssessmentType | null>(
    null,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>("pending");
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);

  const currentQuestion: OnboardingAssessmentQuestion | undefined =
    assessment?.questions[currentIndex];

  // Start assessment on mount
  useEffect(() => {
    const startAssessment = async () => {
      try {
        const data = await api.startAssessment();
        setAssessment(data);
        setTimeLeft(data.questions[0]?.time_limit_seconds || 10);
        setStartTime(Date.now());
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || "Failed to start assessment");
        setIsLoading(false);
      }
    };
    startAssessment();
  }, []);

  // Timer countdown
  useEffect(() => {
    if (answerState !== "pending" || !currentQuestion || isLoading) return;

    timerRef.current = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Time's up!
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex, answerState, isLoading]);

  const handleTimeout = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setAnswerState("timeout");
    const timeTaken = Date.now() - startTime;

    // Submit timeout as null answer
    if (assessment) {
      try {
        await api.submitAssessmentAnswer({
          assessment_id: assessment.id,
          question_id: currentQuestion!.id,
          user_answer: null,
          time_taken_ms: timeTaken,
        });
      } catch (err) {
        console.error("Failed to submit timeout:", err);
      }
    }

    // Auto-advance after short delay
    setTimeout(() => {
      advanceToNext();
    }, 800);
  }, [assessment, currentQuestion, startTime]);

  const handleSelectAnswer = async (answer: string) => {
    if (answerState !== "pending" || !assessment || !currentQuestion) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSelectedAnswer(answer);
    const timeTaken = Date.now() - startTime;
    const normalizeAnswer = (s: string) => s.replace(/\s+/g, "").toLowerCase();
    const isCorrect = normalizeAnswer(answer) === normalizeAnswer(currentQuestion.correct_answer);

    setAnswerState(isCorrect ? "correct" : "incorrect");

    // Submit answer
    try {
      await api.submitAssessmentAnswer({
        assessment_id: assessment.id,
        question_id: currentQuestion.id,
        user_answer: answer,
        time_taken_ms: timeTaken,
      });
    } catch (err) {
      console.error("Failed to submit answer:", err);
    }

    // Auto-advance after quick feedback delay
    setTimeout(() => {
      advanceToNext();
    }, 700);
  };

  const advanceToNext = () => {
    if (!assessment) return;

    if (currentIndex >= assessment.questions.length - 1) {
      // Assessment complete
      navigate(`/assessment/results?id=${assessment.id}`);
    } else {
      // Move to next question
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setAnswerState("pending");
      setTimeLeft(
        assessment.questions[currentIndex + 1]?.time_limit_seconds || 10,
      );
      setStartTime(Date.now());
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400">
            Activating the Trial...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-500">{error}</p>
          <Button
            onClick={() => navigate("/onboarding/goals")}
            variant="outline"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!assessment || !currentQuestion) {
    return null;
  }

  const progress = ((currentIndex + 1) / assessment.questions.length) * 100;
  const timerPercent = (timeLeft / currentQuestion.time_limit_seconds) * 100;
  const isTimerWarning = timeLeft <= 5;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="max-w-xl w-full space-y-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>
              Question {currentIndex + 1} of {assessment.questions.length}
            </span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Timer */}
        <div className="flex justify-center">
          <div
            className={`relative w-20 h-20 rounded-full flex items-center justify-center ${
              isTimerWarning ? "animate-pulse" : ""
            }`}
            style={{
              background: `conic-gradient(${
                isTimerWarning ? "#ef4444" : "#6366f1"
              } ${timerPercent}%, #e5e7eb 0%)`,
            }}
          >
            <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
              <div className="flex items-center gap-1">
                <Clock
                  className={`w-4 h-4 ${
                    isTimerWarning ? "text-red-500" : "text-indigo-500"
                  }`}
                />
                <span
                  className={`text-xl font-bold ${
                    isTimerWarning
                      ? "text-red-500"
                      : "text-gray-900 dark:text-white"
                  }`}
                >
                  {timeLeft}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Question Card */}
        <Card
          className={`border-2 transition-colors ${
            answerState === "correct"
              ? "border-green-500 bg-green-50 dark:bg-green-900/20"
              : answerState === "incorrect" || answerState === "timeout"
                ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          }`}
        >
          <CardContent className="p-6">
            <div className="text-center space-y-2">
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {currentQuestion.type === "estimate" ? "Estimate" : "Solve"}
              </span>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {currentQuestion.problem}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Answer Options */}
        <div className="grid grid-cols-2 gap-3">
          {currentQuestion.options.map((option) => {
            const isSelected = selectedAnswer === option;
            const isCorrectAnswer =
              option.toLowerCase().trim() ===
              currentQuestion.correct_answer.toLowerCase().trim();
            const showCorrect =
              (answerState === "incorrect" || answerState === "timeout") &&
              isCorrectAnswer;
            const showIncorrect =
              answerState === "incorrect" && isSelected && !isCorrectAnswer;

            return (
              <Button
                key={option}
                variant="outline"
                className={`h-16 text-lg font-semibold transition-all ${
                  answerState === "correct" && isSelected
                    ? "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                    : showIncorrect
                      ? "border-red-500 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                      : showCorrect
                        ? "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                        : isSelected
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-white"
                          : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
                onClick={() => handleSelectAnswer(option)}
                disabled={answerState !== "pending"}
              >
                {showCorrect ? (
                  <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                ) : showIncorrect ? (
                  <XCircle className="w-5 h-5 mr-2 text-red-500" />
                ) : answerState === "correct" && isSelected ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : null}
                {option}
              </Button>
            );
          })}
        </div>

        {/* Timeout Message */}
        {answerState === "timeout" && (
          <div className="text-center">
            <p className="text-red-500">The gate closes!</p>
          </div>
        )}
      </div>
    </div>
  );
}
